import { Avatar as BaseAvatar } from '@base-ui-components/react/avatar';
import { cn } from '../cn.js';

export interface AvatarProps extends BaseAvatar.Root.Props {
  /** The person's name. It is the alt text, and its initials are the fallback. */
  name: string;
  src?: string | undefined;
  size?: 'sm' | 'md' | 'lg';
}

const sizes = {
  sm: 'size-(--size-control-sm) text-(length:--font-size-caption)',
  md: 'size-(--size-control-md) text-(length:--font-size-caption)',
  lg: 'size-(--size-control-lg) text-(length:--font-size-body)',
} as const;

/**
 * Somebody's face, or their initials when there is no face.
 *
 * The fallback is initials rather than a generic silhouette, because a board of twelve identical
 * silhouettes tells a supervisor nothing. The name is always the `alt`, so the avatar is never a
 * picture with no caption.
 */
export function Avatar({ className, name, src, size = 'md', ...props }: AvatarProps) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => [...part][0]?.toUpperCase() ?? '')
    .join('');

  return (
    <BaseAvatar.Root
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden',
        'rounded-(--radius-full) bg-(--color-surface-sunken) text-(--color-text-secondary) font-medium',
        sizes[size],
        className,
      )}
      {...props}
    >
      {src === undefined ? null : (
        <BaseAvatar.Image src={src} alt={name} className="size-full object-cover" />
      )}
      {/* `aria-hidden`: the name reaches assistive technology through the image's alt text or
          through whatever labels the row, and initials read aloud are noise. */}
      <BaseAvatar.Fallback aria-hidden="true">{initials}</BaseAvatar.Fallback>
    </BaseAvatar.Root>
  );
}
